import { useMutation } from '@tanstack/react-query';
import { API_ENDPOINTS } from 'Constants/env';
import { usePinnedConversationsLimit } from 'Hooks/config/useMessagingConfig';
import { MobXProviderContext } from 'mobx-react';
import ConversationModel, {
  IConversationModel,
} from 'Models/ConversationModel';
import { useNotificationStore } from 'Modules/notification/store';
import React from 'react';
import { RootStore } from 'Stores/RootStore';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import API from '~/api';
import { useConversationStore } from '../index.store';

const MUTATION_KEY_TOGGLE_PIN_CONVERSATION =
  'conversation.togglePinConversation';

/**
 * Fetch pinned conversations.
 * This function is meant to be used inside conversations list fetchConversations function.
 * @returns Pinned conversations
 */
export const fetchPinnedConversations = async (
  rootStore: RootStore | RootStoreProps
): Promise<ConversationModel[]> => {
  const participantStore =
    (rootStore as RootStore).participantStore ??
    (rootStore as RootStoreProps).participant;

  const { conversationsListFilter } = useConversationStore.getState();

  const conversationsResponse = await API.get<{
    results: IConversationModel[];
  }>(API_ENDPOINTS.ConversationsFavorites);

  let conversations = conversationsResponse.data.results.map(
    (conversationDto) => {
      const conversation = ConversationModel.FromResponseDto(conversationDto);

      if (conversation.grouping === 'OneOnOne') {
        void participantStore.loadConversationParticipantsIfMissing(
          conversation.id
        );
      }

      return conversation;
    }
  );

  if (conversationsListFilter === 'unread') {
    conversations = conversations.filter(
      (conversation) => conversation.unreadCount > 0
    );
  }

  if (conversationsListFilter === 'group') {
    conversations = conversations.filter((conversation) =>
      ['Channel', 'Group'].includes(conversation.grouping)
    );
  }

  return conversations;
};

export const pinConversation = async (
  { person: personStore, preference: preferenceStore }: RootStoreProps,
  conversationId: string,
  pinnedConversationsLimit: number
) => {
  try {
    useConversationStore.getState().setIsPinningOrUnpinning(true);

    const { pinnedConversations } = useConversationStore.getState();

    if (pinnedConversations.length >= pinnedConversationsLimit) {
      useNotificationStore.getState().addNotify({
        type: 'info',
        dismissible: true,
        autoDismiss: true,
        title: 'Information',
        message: `You can only pin up to ${pinnedConversationsLimit} conversations.`,
      });

      return;
    }

    await personStore.waitUntilLoggedIn();

    await API.patch<ConversationModel>(API_ENDPOINTS.Preference, [
      {
        op: 'add',
        path: '/FavoriteConversationIds/-',
        value: conversationId,
      },
    ]);

    void preferenceStore.invalidatePreferenceData();

    useConversationStore
      .getState()
      .setIsConversationPinned(conversationId, true);

    pushToGTMDataLayer('conversation_pin');
  } catch (error) {
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error pinning conversation',
      message: 'We couldn’t pin the conversation. Please try again',
    });
  } finally {
    useConversationStore.getState().setIsPinningOrUnpinning(false);
  }
};

const unpinConversation = async (
  { conversation: conversationStore }: RootStoreProps,
  conversationId: string
) => {
  try {
    useConversationStore.getState().setIsPinningOrUnpinning(true);

    /**
     * Temporarily use old store method, while the new endpoint is not implemented.
     * I don't want 💩 on the new store.
     */
    await conversationStore.removeConversationFromFavoritesPatch(
      conversationId
    );

    useConversationStore
      .getState()
      .setIsConversationPinned(conversationId, false);
  } catch (error) {
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error unpinning conversation',
      message: 'We couldn’t unpin the conversation. Please try again',
    });
  } finally {
    useConversationStore.getState().setIsPinningOrUnpinning(false);
  }
};

export const useTogglePinConversation = () => {
  const rootStore = React.useContext<RootStoreProps>(MobXProviderContext);
  const pinnedConversationsLimit = usePinnedConversationsLimit();

  const { isPinningOrUnpinning, getIsConversationPinned } =
    useConversationStore();

  return useMutation({
    mutationKey: [MUTATION_KEY_TOGGLE_PIN_CONVERSATION],
    mutationFn: async (conversationId: string) => {
      if (isPinningOrUnpinning) {
        return;
      }

      if (getIsConversationPinned(conversationId)) {
        await unpinConversation(rootStore, conversationId);
      } else {
        await pinConversation(
          rootStore,
          conversationId,
          pinnedConversationsLimit
        );
      }
    },
  });
};
